import React from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Space,
  Tag,
  Typography,
  message,
  Collapse
} from 'antd';
import {
  PlusOutlined,
  LinkOutlined,
  SettingOutlined,
  TeamOutlined,
  ApartmentOutlined,
  FormOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { LimsNavIcon } from '../../components/LimsNavIcon';
import {
  MOCK_TEAM_OPEN_DEVICES,
  MOCK_CROSS_TEAM_DEVICES,
  type TeamOpenSharedDevice,
  type CrossTeamSharedDevice
} from './sharedPlatformMocks';

const { Text, Paragraph } = Typography;

const comingSoon = (label: string) => () => {
  message.info(`${label}功能开发中`);
};

function exposureColor(exposure: string): string {
  if (exposure === '已开放' || exposure === '已上架') return 'success';
  if (exposure === '待配置' || exposure === '审批中') return 'warning';
  if (exposure === '已暂停' || exposure === '已下架') return 'default';
  return 'processing';
}

function TeamOpenDeviceCard({ item }: { item: TeamOpenSharedDevice }) {
  return (
    <Card
      className="sp-device-card"
      size="small"
      hoverable
      title={
        <div className="sp-device-card-title">
          <span className="sp-device-card-name">{item.name}</span>
        </div>
      }
      extra={<Tag color={exposureColor(item.exposure)}>{item.exposure}</Tag>}
      actions={[
        <span key="rule" className="sp-device-card-action" onClick={comingSoon('预约规则')}>
          <SettingOutlined /> 规则
        </span>,
        <span key="off" className="sp-device-card-action" onClick={comingSoon('暂停对外')}>
          暂停
        </span>
      ]}
    >
      <div className="sp-device-card-body">
        <div className="sp-device-card-row">
          <Text type="secondary">型号</Text>
          <Text>{item.model}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">位置</Text>
          <Text ellipsis={{ tooltip: item.location }}>{item.location}</Text>
        </div>
        <div className="sp-device-card-row sp-device-card-row--block">
          <Text type="secondary">开放时段</Text>
          <Text>{item.openRule}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">审批</Text>
          <Text>{item.approver}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">接待</Text>
          <Text ellipsis={{ tooltip: item.reception }}>{item.reception}</Text>
        </div>
        <div className="sp-device-card-row sp-device-card-row--block">
          <Text type="secondary">费用</Text>
          <Text>{item.fee}</Text>
        </div>
      </div>
    </Card>
  );
}

function CrossTeamDeviceCard({ item }: { item: CrossTeamSharedDevice }) {
  return (
    <Card
      className="sp-device-card sp-device-card--platform"
      size="small"
      hoverable
      title={
        <div className="sp-device-card-title">
          <span className="sp-device-card-name">{item.name}</span>
        </div>
      }
      extra={<Tag color={exposureColor(item.exposure)}>{item.exposure}</Tag>}
      actions={[
        <span key="rule" className="sp-device-card-action" onClick={comingSoon('平台预约规则')}>
          <FormOutlined /> 规则
        </span>,
        <span key="rm" className="sp-device-card-action" onClick={comingSoon('从平台移除')}>
          下架
        </span>
      ]}
    >
      <div className="sp-device-card-body">
        <div className="sp-device-card-row sp-device-card-row--block">
          <Text type="secondary">所属平台</Text>
          <Text strong>{item.platformName}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">设备团队</Text>
          <Text ellipsis={{ tooltip: item.owningTeam }}>{item.owningTeam}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">型号</Text>
          <Text>{item.model}</Text>
        </div>
        <div className="sp-device-card-row">
          <Text type="secondary">位置</Text>
          <Text ellipsis={{ tooltip: item.location }}>{item.location}</Text>
        </div>
        <div className="sp-device-card-row sp-device-card-row--block">
          <Text type="secondary">平台规则摘要</Text>
          <Text>{item.openRule}</Text>
        </div>
      </div>
    </Card>
  );
}

export function TeamOpenSharedPanel(): React.ReactElement {
  const openCount = MOCK_TEAM_OPEN_DEVICES.filter((d) => d.exposure === '已开放').length;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div className="sp-panel-toolbar">
        <div className="sp-panel-toolbar-left">
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={comingSoon('从仪器库添加共享仪器')}>
              添加共享仪器
            </Button>
            <Button icon={<SettingOutlined />} onClick={comingSoon('批量配置预约规则')}>
              配置预约规则
            </Button>
            <Button icon={<LinkOutlined />} onClick={comingSoon('生成对外预约链接')}>
              对外预约链接
            </Button>
            <Button icon={<CopyOutlined />} onClick={comingSoon('复制团队邀请说明')}>
              复制说明文案
            </Button>
          </Space>
        </div>
        <div className="sp-panel-toolbar-right">
          <Text type="secondary">
            演示数据 · 已开放 <Text strong>{openCount}</Text> / {MOCK_TEAM_OPEN_DEVICES.length} 台
          </Text>
        </div>
      </div>

      <Row gutter={[16, 16]} className="sp-device-grid">
        {MOCK_TEAM_OPEN_DEVICES.map((item) => (
          <Col xs={24} sm={12} lg={8} key={item.id}>
            <TeamOpenDeviceCard item={item} />
          </Col>
        ))}
      </Row>

      <Collapse
        bordered={false}
        className="sp-collapse"
        items={[
          {
            key: 'help',
            label: '本模式说明（与多团队平台互斥）',
            children: (
              <Space direction="vertical" size="small">
                <Paragraph className="sp-muted sp-muted--tight">
                  对外仪器列表、预约规则、审批与接待人、访客链接等将在此管理；同一仪器需通过「归属平台」开关与多团队平台二选一。
                </Paragraph>
                <ul className="sp-list sp-list--compact">
                  <li>外部用户以填写的团队码归属，可加入多个团队（每次需管理员审批）。</li>
                  <li>审批通过后的通知模板将附带接待人与接待时间（站内外）。</li>
                </ul>
              </Space>
            )
          }
        ]}
      />
    </Space>
  );
}

export function CrossTeamSharedPanel(): React.ReactElement {
  const listed = MOCK_CROSS_TEAM_DEVICES.filter((d) => d.exposure === '已上架').length;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div className="sp-panel-toolbar">
        <div className="sp-panel-toolbar-left">
          <Space wrap>
            <Button type="primary" icon={<ApartmentOutlined />} onClick={comingSoon('创建或编辑多团队平台')}>
              平台设置
            </Button>
            <Button icon={<TeamOutlined />} onClick={comingSoon('邀请其他团队加入')}>
              管理团队
            </Button>
            <Button icon={<PlusOutlined />} onClick={comingSoon('向平台添加上架仪器')}>
              添加平台仪器
            </Button>
            <Button icon={<SettingOutlined />} onClick={comingSoon('平台级预约与计费策略')}>
              平台规则
            </Button>
          </Space>
        </div>
        <div className="sp-panel-toolbar-right sp-panel-toolbar-right--stack">
          <Space align="end" direction="vertical" size={0}>
            <Text type="secondary">
              演示平台：<Text strong>长三角大型仪器协作网（演示）</Text>
            </Text>
            <Text type="secondary">
              已上架 <Text strong>{listed}</Text> / {MOCK_CROSS_TEAM_DEVICES.length} 台
            </Text>
          </Space>
        </div>
      </div>

      <Card size="small" className="sp-platform-strip" bordered={false}>
        <div className="sp-platform-strip-inner">
          <LimsNavIcon name="hub" sizePx={22} className="sp-platform-strip-icon" />
          <div>
            <Text strong>多团队共享与「本团队对外」可使用不同的预约规则</Text>
            <Paragraph type="secondary" className="sp-platform-strip-sub">
              下方卡片为模拟跨团队上架设备；正式环境将展示真实团队、审批状态与规则版本。
            </Paragraph>
          </div>
        </div>
      </Card>

      <Row gutter={[16, 16]} className="sp-device-grid">
        {MOCK_CROSS_TEAM_DEVICES.map((item) => (
          <Col xs={24} sm={12} lg={8} key={item.id}>
            <CrossTeamDeviceCard item={item} />
          </Col>
        ))}
      </Row>

      <Collapse
        bordered={false}
        className="sp-collapse"
        items={[
          {
            key: 'help',
            label: '全平台模式说明',
            children: (
              <ul className="sp-list sp-list--compact">
                <li>需填写他方团队码并取得对方管理员同意后，方可加入同一共享平台。</li>
                <li>各团队对自己上架的设备单独配置规则，可与单团队对外策略不同。</li>
              </ul>
            )
          }
        ]}
      />
    </Space>
  );
}
